// client/src/components/Reservations/components/ReservationDetailsModal.jsx
import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import { formatMoney } from '../utils/money';
import EditReservationForm from './EditReservationForm';
import RulesSummary from './RulesSummary';

export default function ReservationDetailsModal({ 
  reservation, 
  isOpen, 
  onClose, 
  onUpdate, 
  onCancel,
  selectedItem,
  onOpenEdit // optional: external handler to open dedicated edit modal
}) {
  const [activeTab, setActiveTab] = useState('details');
  const [showEditForm, setShowEditForm] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setActiveTab('details');
      setShowEditForm(false);
      setShowCancelForm(false);
      setCancelReason('');
    }
  }, [isOpen]);

  if (!isOpen || !reservation) return null;

  // Normalize status (treat 'confirmed' as 'active')
  const normalizeStatus = (s) => {
    const v = String(s || '').toLowerCase();
    return v === 'confirmed' ? 'active' : v;
  };
  const statusBadgeClasses = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
      default:
        return 'bg-red-100 text-red-800';
    }
  };

  // Robust datetime derivation (avoid Invalid DateTime display)
  const deriveDateTime = () => {
    const directIso = reservation.start_at || reservation.start_iso || reservation.slot_start;
    if (directIso) {
  const dt = DateTime.fromISO(String(directIso), { zone: 'utc' });
      if (dt.isValid) return dt;
    }
    const d = reservation.date;
    const t = reservation.time || reservation.start_time || reservation.start_hhmm;
    if (d && t) {
      const dt = DateTime.fromISO(`${d}T${t}`);
      if (dt.isValid) return dt;
    }
    const it = Array.isArray(reservation.items) ? reservation.items[0] : null;
    const itIso = it?.start_at || it?.slot_start || it?.start_iso;
    if (itIso) {
  const dt = DateTime.fromISO(String(itIso), { zone: 'utc' });
      if (dt.isValid) return dt;
    }
    return DateTime.invalid('Invalid Reservation Date');
  };

  const reservationDateTime = deriveDateTime();
  const now = DateTime.utc();
  const isValidDt = reservationDateTime?.isValid;
  const isPast = isValidDt ? reservationDateTime <= now : false;
  // Use selected item's status (or first item) to determine header status
  const primaryItem = Array.isArray(reservation.items) && reservation.items.length ? reservation.items[0] : null;
  const selectedItemObj = (() => {
    if (!Array.isArray(reservation.items)) return null;
    if (!selectedItem) return primaryItem;
    if (selectedItem.itemId) return reservation.items.find(it => String(it.id) === String(selectedItem.itemId)) || primaryItem;
    if (selectedItem.offeringKind && selectedItem.offeringId) {
      return reservation.items.find(it => String(it.offering_kind) === String(selectedItem.offeringKind) && String(it.offering_id) === String(selectedItem.offeringId)) || primaryItem;
    }
    return primaryItem;
  })();
  const headerStatus = normalizeStatus(selectedItemObj?.status || reservation.status || 'pending');
  const canEdit = !isPast && headerStatus !== 'cancelled';
  const canCancel = !isPast && headerStatus !== 'cancelled';
  // When user clicked a bundle child that has no concrete reservation item yet,
  // synthesize a minimal item so the modal can still show details.
  const modeToKind = (mode) => {
    const m = String(mode || '').toLowerCase();
    switch (m) {
      case 'personal_services': return 'ps_service';
      case 'class_based': return 'cb_class';
      case 'modular_seating': return 'ms_table_type';
      case 'accommodation': return 'ac_room_type';
      default: return null;
    }
  };
  const toTimeLabel = (iso) => {
    if (!iso) return isValidDt ? reservationDateTime.toFormat('h:mm a') : '—';
    const dt = DateTime.fromISO(String(iso), { zone: 'utc' });
    return dt.isValid ? dt.toFormat('h:mm a') : '—';
  };

  const toDateLabel = (iso) => {
    if (!iso) return isValidDt ? reservationDateTime.toFormat('MMM dd, yyyy') : '—';
    const dt = DateTime.fromISO(String(iso), { zone: 'utc' });
    return dt.isValid ? dt.toFormat('MMM dd, yyyy') : '—';
  };

  const isAccommodationKind = (kind) => String(kind || '').toLowerCase() === 'ac_room_type';

  // Helpers for accommodation children inside bundles (hotel info)
  const acChildLabels = (c) => {
    const startISO = c?.nights_start || null;
    const endISO = c?.nights_end || null;
    const count = c?.nights_count != null ? Number(c.nights_count) : null;
    const dtStart = startISO ? DateTime.fromISO(String(startISO), { zone: 'utc' }) : null;
    const dtEnd = endISO ? DateTime.fromISO(String(endISO), { zone: 'utc' }) : null;
    let nights = count;
    if (nights == null && dtStart && dtStart.isValid && dtEnd && dtEnd.isValid) {
      const days = dtEnd.startOf('day').diff(dtStart.startOf('day'), 'days').days;
      nights = Math.max(1, Math.round(days));
    }
    const startLabel = dtStart && dtStart.isValid ? dtStart.toFormat('MMM dd, yyyy') : '—';
    const nightsLabel = nights != null ? `${nights} ${nights === 1 ? 'night' : 'nights'}` : '—';
    return { startLabel, nightsLabel };
  };

  const deriveAccommodationInfo = (item) => {
    // Prefer explicit nights range from backend item payload
    const startDateStr = item.ac_nights_start || selectedItem?.childMeta?.nights_start || null;
    const endDateStr = item.ac_nights_end || selectedItem?.childMeta?.nights_end || null;
    const nightsCount = item.ac_nights_count ?? selectedItem?.childMeta?.nights_count ?? null;

    const dtStartDate = startDateStr ? DateTime.fromISO(String(startDateStr), { zone: 'utc' }) : null;
    const dtEndDate = endDateStr ? DateTime.fromISO(String(endDateStr), { zone: 'utc' }) : null;

    // Compose check-in/out times using business settings when available
    const ci = reservation.check_in_after || '15:00:00';
    const co = reservation.check_out_before || '11:00:00';
    const dtCheckIn = dtStartDate && dtStartDate.isValid ? DateTime.fromISO(`${dtStartDate.toISODate()}T${ci}`, { zone: 'utc' }) : null;
    const dtCheckOut = dtEndDate && dtEndDate.isValid ? DateTime.fromISO(`${dtEndDate.toISODate()}T${co}`, { zone: 'utc' }) : null;

    // Fallback nights calculation if count missing but we have both dates
    let nights = nightsCount != null ? Number(nightsCount) : null;
    if (nights == null && dtStartDate && dtStartDate.isValid && dtEndDate && dtEndDate.isValid) {
      const days = dtEndDate.startOf('day').diff(dtStartDate.startOf('day'), 'days').days;
      nights = Math.max(1, Math.round(days));
    }

    return {
      startDateLabel: dtStartDate && dtStartDate.isValid ? dtStartDate.toFormat('MMM dd, yyyy') : '—',
      endDateLabel: dtEndDate && dtEndDate.isValid ? dtEndDate.toFormat('MMM dd, yyyy') : '—',
      checkInLabel: dtCheckIn && dtCheckIn.isValid ? dtCheckIn.toFormat('h:mm a') : '—',
      checkOutLabel: dtCheckOut && dtCheckOut.isValid ? dtCheckOut.toFormat('h:mm a') : '—',
      nights: nights != null ? nights : '—',
    };
  };

  const itemsToShow = (() => {
    const list = Array.isArray(reservation.items) ? reservation.items : [];
    if (!selectedItem) return list;

    // Try exact item id match
    if (selectedItem.itemId) {
      const hit = list.find(it => String(it.id) === String(selectedItem.itemId));
      if (hit) return [hit];
    }
    // Try offering kind + id match
    if (selectedItem.offeringKind && selectedItem.offeringId) {
      const hit = list.find(it => String(it.offering_kind) === String(selectedItem.offeringKind) && String(it.offering_id) === String(selectedItem.offeringId));
      if (hit) return [hit];
    }
    // Fallback: synthesize from childMeta if present
    if (selectedItem.childMeta) {
      const cm = selectedItem.childMeta;
      const kind = selectedItem.offeringKind || modeToKind(cm.mode);
      return [{
        id: `bundle-child:${kind}:${cm.item_id}`,
        offering_id: cm.item_id,
        offering_kind: kind,
        offering_name: cm.name || 'Included service',
        slot_start: cm.slot_start || null,
        slot_end: cm.slot_end || null,
        party_size: cm.party_size != null ? Number(cm.party_size) : null,
        nights: cm.nights != null ? Number(cm.nights) : null,
        employee_id: null,
        employee_name: null,
        description: null,
        final_price: null,
        price: null,
        included_in_bundle: true,
      }];
    }
    // Default to empty (shouldn’t happen)
    return [];
  })();

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      alert('Please provide a reason for cancellation');
      return;
    }

    setLoading(true);
    try {
      await onCancel(reservation.id, cancelReason);
    } catch (err) {
      alert('Failed to cancel reservation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (updates) => {
    setLoading(true);
    try {
      await onUpdate(reservation.id, updates);
      setShowEditForm(false);
    } catch (err) {
      // Improve surfaced error message, including backend rule violations
      const msg = (err && err.message) ? String(err.message) : 'Failed to update reservation. Please try again.';
      alert(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Reservation Details
            </h2>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${statusBadgeClasses(headerStatus)}`}>
              {headerStatus.charAt(0).toUpperCase() + headerStatus.slice(1)}
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {['details', 'rules', 'history'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto max-h-96">
          {activeTab === 'details' && (
            <div className="p-6 space-y-6">
              {/* Business and Service Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {reservation.business_name}
                </h3>
                <div className="text-sm text-gray-600 space-y-1">
                  {reservation.business_address && (
                    <p>{reservation.business_address}</p>
                  )}
                  {reservation.business_phone && (
                    <p>Phone: {reservation.business_phone}</p>
                  )}
                </div>
              </div>



              {/* Services/Items */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Services</h4>
                <div className="space-y-3">
                  {itemsToShow.map((item, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h5 className="font-medium text-gray-900">
                            {item.offering_name || item.guests_json?.offering_name || item.service_name || item.name}
                            {item.status && (
                              <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${statusBadgeClasses(normalizeStatus(item.status))}`}>
                                {normalizeStatus(item.status).charAt(0).toUpperCase() + normalizeStatus(item.status).slice(1)}
                              </span>
                            )}
                          </h5>
                          {item.offering_kind === 'bundle' && Array.isArray(item.bundle_children) && item.bundle_children.length > 0 && (
                            <div className="mt-2">
                              <div className="text-sm text-gray-700 font-medium mb-1">Included services</div>
                              <ul className="pl-3 border-l border-gray-200 space-y-1">
                                {item.bundle_children.map((c, j) => {
                                  const mode = String(c.mode || '').toLowerCase();
                                  const modeLabel = mode === 'personal_services' ? 'Personal service' : mode === 'class_based' ? 'Class' : mode === 'modular_seating' ? 'Table' : mode === 'accommodation' ? 'Room' : 'Service';
                                  const kind = c.offering_kind || (mode === 'personal_services' ? 'ps_service' : mode === 'class_based' ? 'cb_class' : mode === 'modular_seating' ? 'ms_table_type' : mode === 'accommodation' ? 'ac_room_type' : null);
                                  const match = (!c.slot_start && !('party_size' in c)) && kind ? (reservation.items || []).find(x => x && x.offering_kind !== 'bundle' && x.offering_kind === kind && String(x.offering_id) === String(c.item_id)) : null;
                                  const itTime = (() => {
                                    // For accommodation children (hotel), show start date and nights instead of just a time
                                    if (mode === 'accommodation') {
                                      const { startLabel, nightsLabel } = acChildLabels(c);
                                      return `${startLabel} · ${nightsLabel}`;
                                    }
                                    const iso = c.slot_start || match?.slot_start || match?.start_at || match?.start_iso;
                                    const t = match?.time || match?.start_time || match?.start_hhmm;
                                    if (iso) {
                                      const dt = DateTime.fromISO(String(iso), { zone: 'utc' });
                                      return dt.isValid ? dt.toFormat('h:mm a') : '—';
                                    }
                                    if (t) {
                                      try {
                                        const baseDate = isValidDt ? reservationDateTime.toISODate() : (reservation.date || '');
                                        const dt = baseDate ? DateTime.fromISO(`${baseDate}T${t}`) : DateTime.invalid('no-base');
                                        return dt.isValid ? dt.toFormat('h:mm a') : t;
                                      } catch { return t; }
                                    }
                                    return isValidDt ? reservationDateTime.toFormat('h:mm a') : '—';
                                  })();
                                  const guests = ('party_size' in c) ? Number(c.party_size) : (match ? Number(match.party_size || match.guests || 1) : null);
                                  return (
                                    <li key={`bundle-child-${index}-${j}`} className="flex items-center justify-between gap-3">
                                      <div className="min-w-0 flex items-center gap-2">
                                        <span className="inline-block text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200 whitespace-nowrap">{modeLabel}</span>
                                        <div className="text-gray-700 truncate">{c.name || 'Included service'}</div>
                                      </div>
                                      <div className="flex items-center gap-3 flex-shrink-0 text-sm">
                                        <span className="text-gray-600 whitespace-nowrap">{itTime}</span>
                                        {guests != null && (
                                          <span className="text-gray-900 font-medium whitespace-nowrap">{guests} {guests === 1 ? 'guest' : 'guests'}</span>
                                        )}
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                          {item.description && (
                            <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                          )}
                          {isAccommodationKind(item.offering_kind) ? (
                            <div className="flex flex-col gap-1 mt-2 text-sm text-gray-600">
                              {(() => {
                                const ac = deriveAccommodationInfo(item);
                                return (
                                  <>
                                    <div className="flex flex-wrap items-center gap-4">
                                      <span>Guests: {Number(item.party_size || item.guests || 1)}</span>
                                      <span>Start date: {ac.startDateLabel}</span>
                                      <span>Nights: {ac.nights}</span>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-4">
                                      <span>End date: {ac.endDateLabel}</span>
                                      <span>Check-in: {ac.checkInLabel}</span>
                                      <span>Check-out: {ac.checkOutLabel}</span>
                                    </div>
                                    {item.employee_name && (
                                      <div className="flex flex-wrap items-center gap-4">
                                        <span>Provider: {item.employee_name}</span>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          ) : (
                            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
                              <span>Guests: {Number(item.party_size || item.guests || 1)}</span>
                              <span>Time: {(() => {
                                const itIso = item.start_at || item.slot_start || item.start_iso;
                                const itTime = item.time || item.start_time || item.start_hhmm;
                                if (itIso) {
                                  const dt = DateTime.fromISO(String(itIso), { zone: 'utc' });
                                  return dt.isValid ? dt.toFormat('h:mm a') : '—';
                                }
                                if (itTime) {
                                  try {
                                    const baseDate = isValidDt ? reservationDateTime.toISODate() : (reservation.date || '');
                                    const dt = baseDate ? DateTime.fromISO(`${baseDate}T${itTime}`) : DateTime.invalid('no-base');
                                    return dt.isValid ? dt.toFormat('h:mm a') : itTime;
                                  } catch {
                                    return itTime;
                                  }
                                }
                                // If synthesized bundle child provided slot_start, use it
                                if (selectedItem?.childMeta?.slot_start) {
                                  return toTimeLabel(selectedItem.childMeta.slot_start);
                                }
                                return isValidDt ? reservationDateTime.toFormat('h:mm a') : '—';
                              })()}</span>
                              <span>Duration: {item.duration || 'N/A'}</span>
                              {item.employee_name && (
                                <span>Provider: {item.employee_name}</span>
                              )}
                            </div>
                          )}
                        </div>
                        {item.included_in_bundle ? (
                          <div className="text-right">
                            <span className="inline-block text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200">Included in bundle</span>
                          </div>
                        ) : ((item.final_price ?? item.price) != null && (
                          <div className="text-right">
                            <p className="font-medium text-gray-900">
                              {formatMoney(Number(item.final_price ?? item.price) || 0, reservation.currency)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Special Requests */}
                  {reservation.special_requests && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Special Requests</h4>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-gray-700">{reservation.special_requests}</p>
                  </div>
                </div>
              )}

              {/* Payment Information */}
              {reservation.total_amount && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Payment Summary</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal</span>
                      <span>{formatMoney((reservation.total_amount - (reservation.fees_amount || 0)) / 100, reservation.currency)}</span>
                    </div>
                    {reservation.fees_amount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Fees & Taxes</span>
                        <span>{formatMoney(reservation.fees_amount / 100, reservation.currency)}</span>
                      </div>
                    )}
                    <div className="border-t border-gray-200 pt-2">
                      <div className="flex justify-between font-medium">
                        <span>Total</span>
                        <span>{formatMoney(reservation.total_amount / 100, reservation.currency)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Contact Information */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Contact Information</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center text-sm">
                    <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                    <span>{reservation.customer_email || 'N/A'}</span>
                  </div>
                  {reservation.customer_phone && (
                    <div className="flex items-center text-sm">
                      <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span>{reservation.customer_phone}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'rules' && (
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Reservation Policies</h3>

              {/* Helper: amount label */}
              {(() => {
                const currency = reservation.currency || 'EUR';

                const list = itemsToShow; // show policies for the current item(s) context
                const anyPolicies = (it) => {
                  const snap = it?.policy_snapshot_json || {};
                  return !!(snap.fee_policy || snap.edit_rule || (Array.isArray(snap.cancellation_rules) && snap.cancellation_rules.length > 0));
                };

                return (
                  <div className="space-y-5">
                    {list.length === 0 && (
                      <div className="text-gray-600 text-sm">No items to show.</div>
                    )}

                    {list.map((it, idx) => {
                      const snap = it?.policy_snapshot_json || {};
                      const fee = snap?.fee_policy || null;
                      const edit = snap?.edit_rule || null;
                      const maxDateShiftDays = (snap?.max_date_shift_days != null) ? snap.max_date_shift_days : (edit?.max_date_shift_days != null ? edit.max_date_shift_days : null);
                      const has = anyPolicies(it);
                      return (
                        <div key={`policy-${idx}`} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="px-4 py-3 bg-gray-50 flex items-center justify-between">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {it.offering_name || it.guests_json?.offering_name || it.service_name || it.name || 'Item'}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {String(it.offering_kind || '').replace(/_/g, ' ')}
                              </div>
                            </div>
                            <div className="ml-3 text-xs">
                              {has ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">Policies available</span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">No policies</span>
                              )}
                            </div>
                          </div>

                          <div className="p-4">
                            <RulesSummary
                              feePolicy={fee}
                              editRule={edit}
                              cancellationRules={snap?.cancellation_rules || []}
                              currency={currency}
                              maxDateShiftDays={maxDateShiftDays}
                            />
                          </div>
                        </div>
                      );
                    })}

                    {/* Business-level policies as extra info */
                    }
                    {(reservation.business_policies || reservation.cancellation_policy) && (
                      <div className="mt-6 space-y-3">
                        {reservation.business_policies && (
                          <div className="prose prose-sm max-w-none">
                            <div dangerouslySetInnerHTML={{ __html: reservation.business_policies }} />
                          </div>
                        )}
                        {reservation.cancellation_policy && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <h4 className="font-medium text-yellow-800 mb-2">Cancellation Policy</h4>
                            <p className="text-sm text-yellow-700">{reservation.cancellation_policy}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Reservation History</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                  <div>
                    <p className="font-medium text-gray-900">Reservation Created</p>
                    <p className="text-sm text-gray-600">
                      {reservation.created_at ? DateTime.fromISO(reservation.created_at).toFormat('MMM dd, yyyy h:mm a') : 'N/A'}
                    </p>
                  </div>
                </div>
                
                {reservation.updated_at && reservation.updated_at !== reservation.created_at && (
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                    <div>
                      <p className="font-medium text-gray-900">Last Updated</p>
                      <p className="text-sm text-gray-600">
                        {DateTime.fromISO(reservation.updated_at).toFormat('MMM dd, yyyy h:mm a')}
                      </p>
                    </div>
                  </div>
                )}

                {reservation.status === 'cancelled' && (
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                    <div>
                      <p className="font-medium text-gray-900">Reservation Cancelled</p>
                      <p className="text-sm text-gray-600">
                        {reservation.cancelled_at ? DateTime.fromISO(reservation.cancelled_at).toFormat('MMM dd, yyyy h:mm a') : 'N/A'}
                      </p>
                      {reservation.cancellation_reason && (
                        <p className="text-sm text-gray-600 mt-1">
                          Reason: {reservation.cancellation_reason}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="border-t border-gray-200 px-6 py-4">
          {showCancelForm ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for cancellation
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Please provide a reason for cancelling this reservation..."
                />
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleCancel}
                  disabled={loading || !cancelReason.trim()}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Cancelling...' : 'Confirm Cancellation'}
                </button>
                <button
                  onClick={() => setShowCancelForm(false)}
                  disabled={loading}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Keep Reservation
                </button>
              </div>
            </div>
          ) : (
            <div className="flex justify-between">
              <div className="flex space-x-3">
                {canEdit && (
                  <button
                    onClick={() => {
                      if (typeof onOpenEdit === 'function') {
                        onOpenEdit(reservation);
                      } else {
                        // Fallback: keep inline form if external modal not provided
                        setShowEditForm(true);
                      }
                    }}
                    className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Edit Reservation
                  </button>
                )}
                {canCancel && (
                  <button
                    onClick={() => setShowCancelForm(true)}
                    className="bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Cancel Reservation
                  </button>
                )}
              </div>
              <button
                onClick={onClose}
                className="bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Close
              </button>
            </div>
          )}

          {/* Fallback inline editor when external modal handler not provided */}
          {showEditForm && (
            <div className="mt-4">
              <EditReservationForm
                reservation={reservation}
                onSave={handleEdit}
                onCancel={() => setShowEditForm(false)}
                loading={loading}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}